import Badge from '@shared/components/Badge/Badge';
import cx from 'classnames';
import { PropsWithChildren } from 'react';
import { useLocation } from 'react-router-dom';

import LinkButton from 'components/LinkButton/LinkButton';
import useFeatureFlagContext from 'contexts/FeatureFlagProvider/useFeatureFlagContext';
import { FeatureFlag } from 'utils/Constants';
import { IconType } from 'utils/types';

interface SidebarLinkProps {
  className?: string;
  icon: IconType;
  path?: string;
  isExternal?: boolean;
  dataTestId?: string;
  isDisabled?: boolean;
  badgeValue?: number;
  featureFlag?: FeatureFlag;
}

const SidebarLink = ({
  children,
  className,
  icon: Icon,
  path,
  isDisabled = false,
  isExternal = false,
  badgeValue,
  dataTestId,
  featureFlag,
}: PropsWithChildren<SidebarLinkProps>) => {
  const { pathname } = useLocation();
  const { isEnabled } = useFeatureFlagContext();

  const isSelected = pathname === path;
  const isFeatureEnabled = featureFlag ? isEnabled(featureFlag) : true;

  if (!isFeatureEnabled) return null;

  return (
    <LinkButton
      variant="gray80"
      link={(!isDisabled && path) || undefined}
      disabled={isDisabled}
      className={cx(
        'hover:bg-gray-30 relative flex select-none flex-col items-center whitespace-nowrap rounded-xl p-2 md:flex-row md:space-x-2 md:p-2.5',
        { 'text-primary-70 bg-primary-10 hover:bg-primary-20': isSelected },
        className
      )}
      data-testid={dataTestId}
      isExternal={isExternal}
    >
      <Icon className="stroke-primary-70 h-5 w-5 shrink-0 stroke-2" />
      <div className="flex w-full justify-between space-x-1">
        <span className="text-size-xs md:text-size-sm font-normal md:font-medium">{children}</span>
        {!!badgeValue && (
          <Badge size="sm" className="md:min-h-[24px] md:min-w-[24px] md:rounded-lg">
            {badgeValue}
          </Badge>
        )}
      </div>
    </LinkButton>
  );
};

export default SidebarLink;
